import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Webhook, Plus, Trash2, Edit3, CheckCircle, XCircle } from 'lucide-react'
import { useAlertStore } from '@/stores/stores'
import { AppShell, Topbar, Modal, ConfirmDialog, EmptyState, PageLoader, ToastContainer } from '../components/layout/Layout'
import { useForm } from 'react-hook-form'
import api from '../services/api'

const webhooksApi = {
    list:   ()         => api.get('/admin/webhooks'),
    create: (data)     => api.post('/admin/webhooks', data),
    update: (id, data) => api.put(`/admin/webhooks/${id}`, data),
    delete: (id)       => api.delete(`/admin/webhooks/${id}`),
}

export function WebhooksPage() {
    const qc = useQueryClient()
    const { addToast }  = useAlertStore()
    const [createOpen, setCreateOpen] = useState(false)
    const [editItem, setEditItem]     = useState(null)
    const [deleteId, setDeleteId]     = useState(null)
    const { register, handleSubmit, reset, setValue } = useForm()

    const { data: webhooks = [], isLoading } = useQuery({
        queryKey: ['webhooks'],
        queryFn:  () => webhooksApi.list().then(r => r.data),
    })

    const openEdit = (w) => {
        setEditItem(w)
        setValue('name', w.name)
        setValue('url', w.url)
        setValue('events', w.events?.join(', '))
        setCreateOpen(true)
    }

    const saveWebhook = async (data) => {
        try {
            const payload = { ...data, events: data.events ? data.events.split(',').map(e => e.trim()).filter(Boolean) : [] }
            if (editItem) {
                await webhooksApi.update(editItem.id, payload)
                addToast({ type: 'success', title: 'Webhook aktualizován' })
            } else {
                await webhooksApi.create(payload)
                addToast({ type: 'success', title: 'Webhook vytvořen' })
            }
            qc.invalidateQueries(['webhooks'])
            setCreateOpen(false)
            setEditItem(null)
            reset()
        } catch (e) {
            addToast({ type: 'error', title: 'Chyba uložení webhoku' })
        }
    }

    const deleteWebhook = async (id) => {
        try {
            await webhooksApi.delete(id)
            qc.invalidateQueries(['webhooks'])
            addToast({ type: 'success', title: 'Webhook smazán' })
            setDeleteId(null)
        } catch (e) {
            addToast({ type: 'error', title: 'Chyba mazání' })
        }
    }

    return (
        <AppShell>
            <Topbar
                title="Webhooky"
                subtitle="Notifikace externích systémů při událostech platformy"
                actions={
                    <button className="btn btn-primary btn-sm" onClick={() => { reset(); setEditItem(null); setCreateOpen(true) }}>
                        <Plus size={14} /> Nový webhook
                    </button>
                }
            />
            <main className="app-main">
                {isLoading ? <PageLoader /> : webhooks.length > 0 ? (
                    <div className="card">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Název</th>
                                        <th>URL</th>
                                        <th>Události</th>
                                        <th>Stav</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {webhooks.map(w => (
                                        <tr key={w.id}>
                                            <td style={{ fontWeight: 500 }}>{w.name}</td>
                                            <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                {w.url}
                                            </td>
                                            <td>
                                                {w.events?.map(e => (
                                                    <span key={e} className="badge badge-info" style={{ marginRight: 4 }}>{e}</span>
                                                ))}
                                            </td>
                                            <td>
                                                {w.active
                                                    ? <span style={{ color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Aktivní</span>
                                                    : <span style={{ color: 'var(--red)',   display: 'flex', alignItems: 'center', gap: 4 }}><XCircle   size={14} /> Neaktivní</span>
                                                }
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(w)}>
                                                        <Edit3 size={14} />
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(w.id)}
                                                        style={{ color: 'var(--red)' }}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <EmptyState
                        icon={Webhook}
                        title="Žádné webhooky"
                        text="Přidejte webhook pro notifikaci externích systémů při událostech (offline obrazovka, nové médium...)"
                        action={
                            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
                                <Plus size={14} /> Přidat webhook
                            </button>
                        }
                    />
                )}
            </main>

            <Modal
                open={createOpen}
                onClose={() => { setCreateOpen(false); setEditItem(null); reset() }}
                title={editItem ? 'Upravit webhook' : 'Nový webhook'}
                footer={
                    <>
                        <button className="btn btn-secondary" onClick={() => { setCreateOpen(false); setEditItem(null); reset() }}>Zrušit</button>
                        <button className="btn btn-primary" onClick={handleSubmit(saveWebhook)}>
                            {editItem ? 'Uložit' : 'Vytvořit'}
                        </button>
                    </>
                }
            >
                <div className="form-group">
                    <label className="form-label">Název</label>
                    <input {...register('name', { required: true })} className="form-input" placeholder="ERP notifikace" autoFocus />
                </div>
                <div className="form-group">
                    <label className="form-label">URL</label>
                    <input {...register('url', { required: true })} className="form-input" placeholder="https://erp.firma.cz/webhook" type="url" />
                </div>
                <div className="form-group">
                    <label className="form-label">Události (oddělené čárkou)</label>
                    <input {...register('events')} className="form-input"
                        placeholder="screen.offline, media.uploaded, alert.created" />
                    <div className="form-hint">Dostupné: screen.offline, screen.online, media.uploaded, alert.created, playlist.changed</div>
                </div>
            </Modal>

            <ConfirmDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={() => deleteWebhook(deleteId)}
                title="Smazat webhook"
                message="Webhook bude trvale smazán."
                danger
            />
            <ToastContainer />
        </AppShell>
    )
}
